export function renderAbout() {
  return `
    <section id="about" class="bg-sand-sheet text-neutral-900 py-20 md:py-24 px-6 lg:px-16 relative border-b border-black/15">
      <div class="max-w-7xl mx-auto">
        <!-- Section Index Row -->
        <div class="flex flex-wrap items-center justify-between border-b border-black/15 pb-4 mb-12 font-mono text-xs tracking-widest uppercase text-neutral-700">
          <span class="font-bold text-black tracking-widest">01 / ABOUT ME</span>
          <span class="hidden sm:inline tracking-widest text-neutral-600">SRI LANKA BASED • BUILDING WORLDWIDE</span>
          <span class="text-neutral-600 tracking-widest">CODE · GRAPHICS · SYSTEMS</span>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <!-- Left Column: Big Headline & Narrative -->
          <div class="lg:col-span-6 space-y-6">
            <h2 class="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight text-neutral-950 leading-[0.95] font-display">
              Hello! I'm <br/>
              <span class="font-serif italic font-normal text-neutral-900 tracking-tight">Ranjika Nethpriya.</span>
            </h2>

            <div class="inline-block bg-neutral-950 text-[#FAF8F5] font-mono text-xs px-3 py-1.5 tracking-widest uppercase font-semibold">
              BUILDING THINGS THAT MOVE, THINK &amp; WORK.
            </div>

            <div class="space-y-4 text-base sm:text-lg text-neutral-800 font-light leading-relaxed pt-1">
              <p>
                I'm a developer interested in the space where <strong class="font-semibold text-black">software, graphics and creative technology</strong> meet.
              </p>
              <p>
                I like taking an idea apart, understanding how it works, and building it back into something useful.
              </p>
              <p class="text-sm md:text-base text-neutral-700">
                From web applications and desktop tools to <strong class="font-medium text-neutral-900">3D graphics, shaders, game development and interactive experiences</strong>, I'm usually experimenting with something new.
              </p>
              <p class="text-sm md:text-base text-neutral-700">
                I care about understanding the system behind the interface — not just making something that looks good, but making it actually work.
              </p>
            </div>

            <!-- Where Curiosity Began Box -->
            <div class="mt-8 pt-6 border-t border-black/15 space-y-2.5">
              <span class="font-mono text-xs font-bold uppercase tracking-widest text-neutral-700 block">
                WHERE CURIOSITY BEGAN
              </span>
              <div class="font-mono text-xs font-semibold text-neutral-900 tracking-wider">
                CODE → GRAPHICS → SYSTEMS → EXPERIMENTS
              </div>
              <p class="font-serif italic text-sm text-neutral-700 pt-0.5">
                Always learning. Always building. Still curious.
              </p>
            </div>

            <!-- Experience & Education Sub-Section -->
            <div class="mt-8 pt-6 border-t border-black/15">
              <h2 class="font-mono text-xs font-bold uppercase tracking-widest text-neutral-900 mb-4 flex items-center justify-between">
                <span>01.1 // EXPERIENCE &amp; EDUCATION</span>
                <span class="text-[10px] text-neutral-500 font-normal">TIMELINE</span>
              </h2>
              <div class="space-y-3 font-mono text-xs text-neutral-800">
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-baseline border-b border-black/10 pb-2.5">
                  <div>
                    <span class="font-bold text-neutral-950">Freelance Web &amp; Graphic Designer</span>
                    <span class="block text-neutral-600 font-light text-[11px]">Independent Creative Work (Aphrodiva) • UI/UX &amp; Web Systems</span>
                  </div>
                  <span class="text-neutral-500 text-[11px] font-semibold mt-0.5 sm:mt-0">2025–26</span>
                </div>
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-baseline border-b border-black/10 pb-2.5">
                  <div>
                    <span class="font-bold text-neutral-950">Founder &amp; Designer</span>
                    <span class="block text-neutral-600 font-light text-[11px]">VULCERM Online Clothing • Brand Identity &amp; Digital Storefront</span>
                  </div>
                  <span class="text-neutral-500 text-[11px] font-semibold mt-0.5 sm:mt-0">2026</span>
                </div>
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-baseline">
                  <div>
                    <span class="font-bold text-neutral-950">B.Sc. IT Undergraduate</span>
                    <span class="block text-neutral-600 font-light text-[11px]">Rajarata University of Sri Lanka • Information Technology</span>
                  </div>
                  <span class="inline-flex items-center gap-1.5 text-emerald-800 font-semibold text-[11px] mt-0.5 sm:mt-0">
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                    <span>NOW</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: What I Work With (Structured Categories) -->
          <div class="lg:col-span-6 space-y-5 bg-black/[0.035] p-6 sm:p-8 rounded-none border border-black/15">
            <div class="flex justify-between items-center border-b border-black/15 pb-3 mb-2">
              <h2 class="font-mono text-xs font-bold uppercase tracking-widest text-neutral-900">WHAT I WORK WITH // SKILLS &amp; STACK</h2>
              <span class="font-mono text-[10px] text-neutral-500 uppercase">TECH INVENTORY</span>
            </div>

            <div class="space-y-4 font-mono">
              <!-- Web Development -->
              <div class="border-b border-black/10 pb-3">
                <span class="text-[10px] font-bold text-neutral-600 uppercase tracking-wider block mb-1.5">WEB DEVELOPMENT</span>
                <div class="flex flex-wrap gap-1.5 text-[11px] text-neutral-900">
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">React</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Next.js</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">JavaScript</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">TypeScript</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Node.js</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Vite</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Tailwind CSS</span>
                </div>
              </div>

              <!-- Applications -->
              <div class="border-b border-black/10 pb-3">
                <span class="text-[10px] font-bold text-neutral-600 uppercase tracking-wider block mb-1.5">APPLICATIONS</span>
                <div class="flex flex-wrap gap-1.5 text-[11px] text-neutral-900">
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Electron</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Tauri</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">React Native</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Firebase</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">MongoDB</span>
                </div>
              </div>

              <!-- Graphics & Creative Tech -->
              <div class="border-b border-black/10 pb-3">
                <span class="text-[10px] font-bold text-neutral-600 uppercase tracking-wider block mb-1.5">GRAPHICS &amp; CREATIVE TECHNOLOGY</span>
                <div class="flex flex-wrap gap-1.5 text-[11px] text-neutral-900">
                  <span class="px-2 py-0.5 bg-neutral-900 text-stark border border-black">Three.js</span>
                  <span class="px-2 py-0.5 bg-neutral-900 text-stark border border-black">WebGL</span>
                  <span class="px-2 py-0.5 bg-neutral-900 text-stark border border-black">GLSL</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Shaders</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Blender</span>
                </div>
              </div>

              <!-- Game Development -->
              <div class="border-b border-black/10 pb-3">
                <span class="text-[10px] font-bold text-neutral-600 uppercase tracking-wider block mb-1.5">GAME DEVELOPMENT</span>
                <div class="flex flex-wrap gap-1.5 text-[11px] text-neutral-900">
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Godot</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">GDScript</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Game Systems</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Physics</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">3D</span>
                </div>
              </div>

              <!-- Programming -->
              <div class="border-b border-black/10 pb-3">
                <span class="text-[10px] font-bold text-neutral-600 uppercase tracking-wider block mb-1.5">PROGRAMMING</span>
                <div class="flex flex-wrap gap-1.5 text-[11px] text-neutral-900">
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15 font-bold">C</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15 font-bold">C++</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15 font-bold">C#</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Python</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">JavaScript</span>
                </div>
              </div>

              <!-- Tools -->
              <div>
                <span class="text-[10px] font-bold text-neutral-600 uppercase tracking-wider block mb-1.5">TOOLS</span>
                <div class="flex flex-wrap gap-1.5 text-[11px] text-neutral-900">
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Git</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">GitHub</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">VS Code</span>
                  <span class="px-2 py-0.5 bg-white/85 border border-black/15">Krita</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  `;
}
